import {
  WSElement,
  WSElementProps,
  WSPanel,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import { IntegrationResourceType } from "./types";
import {
  AccountingCustomer,
  AccountingItem,
  AccountingVendor,
  LedgerAccount
} from "@wingspanhq/wingspan-api-sdk";
import { InfoItem } from "../ClientInfoPanel/InfoItem";
import { QuickbooksHistory } from "../../../../Invoices/components/QuickbooksHistory/QuickbooksHistory";
import { selectorAccountingCustomerLabel } from "../../../Integrations/selectors/selectorAccountingCustomerLabel";
import { useEditPayeeIntegrationModal } from "./EditPayeeIntegrationModal";
import { useEditPayerIntegrationModal } from "./EditPayerIntegrationModal";

export interface IntegrationsSectionProps extends WSElementProps {
  resourceType: IntegrationResourceType;
  isInactive: boolean;
  /** Both branches read v3 accounting reference data. */
  payerIntegration?: {
    payerId: string;
    engagementId?: string;
    customer?: AccountingCustomer;
    item?: AccountingItem;
    defaultItem?: AccountingItem;
    /**
     * The stored mapping ids. Passed alongside the resolved records so the panel
     * can tell "nothing is mapped" apart from "something is mapped but no longer
     * exists in the connection's reference data" — reporting the latter as
     * unmapped would hide a real mapping.
     */
    customerId?: string;
    itemId?: string;
  };
  payeeIntegration?: {
    payeeId: string;
    engagementId?: string;
    vendor?: AccountingVendor;
    expenseAccount?: LedgerAccount;
    defaultAccount?: LedgerAccount;
    /** The stored mapping ids — same purpose as the payer branch's pair above. */
    vendorId?: string;
    expenseAccountId?: string;
  };
}

export const IntegrationsSection: React.FC<IntegrationsSectionProps> = ({
  resourceType,
  isInactive,
  payerIntegration,
  payeeIntegration,
  ...elementProps
}) => {
  const updatePayeeModal = useEditPayeeIntegrationModal();
  const updatePayerModal = useEditPayerIntegrationModal();

  const onEdit = () => {
    if (resourceType === IntegrationResourceType.Payer) {
      updatePayerModal.open({
        payerId: payerIntegration?.payerId!,
        engagementId: payerIntegration?.engagementId,
        itemId: payerIntegration?.item?.id ?? payerIntegration?.itemId,
        customerId:
          payerIntegration?.customer?.id ?? payerIntegration?.customerId
      });
    } else {
      updatePayeeModal.open({
        payeeId: payeeIntegration?.payeeId!,
        engagementId: payeeIntegration?.engagementId,
        vendorId: payeeIntegration?.vendor?.id ?? payeeIntegration?.vendorId,
        expenseAccountId:
          payeeIntegration?.expenseAccount?.id ??
          payeeIntegration?.expenseAccountId
      });
    }
  };

  return (
    <WSElement {...elementProps}>
      <WSSectionToolbar
        title="Integration mappings"
        button={{
          label: "Edit",
          onClick: onEdit,
          disabled: isInactive
        }}
      />

      <WSPanel>
        <InfoItem
          mb="XL"
          label={
            resourceType === IntegrationResourceType.Payer
              ? "QBO Customer"
              : "QBO Vendor"
          }
          value={
            resourceType === IntegrationResourceType.Payer
              ? payerIntegration?.customer
                ? selectorAccountingCustomerLabel(payerIntegration.customer)
                : payerIntegration?.customerId
                  ? `Mapped to ${payerIntegration.customerId} (not found in QuickBooks)`
                  : "Not mapped"
              : payeeIntegration?.vendor
                ? selectorAccountingCustomerLabel(payeeIntegration.vendor)
                : payeeIntegration?.vendorId
                  ? `Mapped to ${payeeIntegration.vendorId} (not found in QuickBooks)`
                  : "Not mapped"
          }
        />

        <InfoItem
          label={
            resourceType === IntegrationResourceType.Payer
              ? "Default QBO Item"
              : "Default QBO Expense Account"
          }
          value={
            resourceType === IntegrationResourceType.Payer
              ? payerIntegration?.item
                ? payerIntegration?.item.displayName
                : payerIntegration?.itemId
                  ? `Mapped to ${payerIntegration.itemId} (not found in QuickBooks)`
                  : payerIntegration?.defaultItem
                    ? `Not mapped, use default "${payerIntegration.defaultItem.displayName}"`
                    : "Not mapped"
              : payeeIntegration?.expenseAccount
                ? payeeIntegration.expenseAccount.displayName
                : payeeIntegration?.expenseAccountId
                  ? `Mapped to ${payeeIntegration.expenseAccountId} (not found in QuickBooks)`
                  : payeeIntegration?.defaultAccount
                    ? `Not mapped, use default "${payeeIntegration.defaultAccount.displayName}"`
                    : "Not mapped"
          }
        />
      </WSPanel>

      <QuickbooksHistory
        Wrapper={WSPanel}
        noBorder
        entityId={
          (resourceType === IntegrationResourceType.Payer
            ? payerIntegration?.payerId
            : payeeIntegration?.payeeId) as string
        }
      />
    </WSElement>
  );
};
